import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { AxiosError } from "axios";
import {
    cancelCompanyAnnualPlan,
    extendCompanyTrial,
    getAdminBillingCompanies,
    getCompanyBillingEvents,
    setCompanyAnnualPlan,
    setCompanyBillingStatus,
    startCompanyTrial,
    updateAdminBillingCompany,
    updateAdminBillingDefaults,
} from "../app/apis/subRatingsAPI";
import { IApiErrorBody, IErrorState } from "../app/interfaces/IApiResponses";
import {
    IAdminBillingCompaniesResponse,
    IAdminBillingCompany,
    IAdminCompanyBillingUpdate,
    IAdminExtendRequest,
    IAdminExtendResponse,
    IAnnualPlanRequest,
    IBillingDefaults,
    IBillingEvent,
} from "../app/interfaces/IBilling";
import { RootState } from "../app/store";

/*
* The Sub Ratings billing admin: the whole customer list, the account-wide
* offer, and per-company pricing and dates. Staff only - every endpoint behind
* this slice is gated on is_staff, so a customer hitting these routes gets a
* 403 rather than someone else's numbers.
*/
export interface IAdminBillingSlice {
    status: 'idle' | 'loading' | 'failed';
    savingStatus: 'idle' | 'saving' | 'failed';
    defaults: IBillingDefaults | null;
    companies: IAdminBillingCompany[];
    events: IBillingEvent[];
    eventsCompanyId: number | null;
    error: undefined | IErrorState;
}

const initialState: IAdminBillingSlice = {
    status: 'idle',
    savingStatus: 'idle',
    defaults: null,
    companies: [],
    events: [],
    eventsCompanyId: null,
    error: undefined,
};

const asError = (err: unknown): IErrorState => {
    const error = err as AxiosError<IApiErrorBody>;
    return { reason: error.response?.data['reason'], status: error.response?.status };
};

export const getAdminBillingThunk = createAsyncThunk(
    'adminBilling/getCompanies',
    async (search: string | undefined, { rejectWithValue }) => {
        try {
            return await getAdminBillingCompanies(search);
        } catch (err) {
            return rejectWithValue(asError(err));
        }
    }
);

export const updateBillingDefaultsThunk = createAsyncThunk(
    'adminBilling/updateDefaults',
    async (defaults: Partial<IBillingDefaults>, { rejectWithValue }) => {
        try {
            return await updateAdminBillingDefaults(defaults);
        } catch (err) {
            return rejectWithValue(asError(err));
        }
    }
);

export const updateCompanyBillingThunk = createAsyncThunk(
    'adminBilling/updateCompany',
    async (
        { companyId, update }: { companyId: number, update: IAdminCompanyBillingUpdate },
        { rejectWithValue },
    ) => {
        try {
            return await updateAdminBillingCompany(companyId, update);
        } catch (err) {
            return rejectWithValue(asError(err));
        }
    }
);

export const extendCompanyTrialThunk = createAsyncThunk(
    'adminBilling/extendTrial',
    async (
        { companyId, request }: { companyId: number, request: IAdminExtendRequest },
        { rejectWithValue },
    ) => {
        try {
            return await extendCompanyTrial(companyId, request);
        } catch (err) {
            return rejectWithValue(asError(err));
        }
    }
);

export const setCompanyBillingStatusThunk = createAsyncThunk(
    'adminBilling/setStatus',
    async ({ companyId, status }: { companyId: number, status: string }, { rejectWithValue }) => {
        try {
            return await setCompanyBillingStatus(companyId, status);
        } catch (err) {
            return rejectWithValue(asError(err));
        }
    }
);

export const startCompanyTrialThunk = createAsyncThunk(
    'adminBilling/startTrial',
    async ({ companyId, days }: { companyId: number, days?: number }, { rejectWithValue }) => {
        try {
            return await startCompanyTrial(companyId, days);
        } catch (err) {
            return rejectWithValue(asError(err));
        }
    }
);

/*
* Striking a deal, or repricing one. The API decides which - calling this for a
* company that already has a plan reprices it rather than opening a second
* subscription - so the admin form only needs the one action.
*/
export const setAnnualPlanThunk = createAsyncThunk(
    'adminBilling/setAnnualPlan',
    async (
        { companyId, request }: { companyId: number, request: IAnnualPlanRequest },
        { rejectWithValue },
    ) => {
        try {
            return await setCompanyAnnualPlan(companyId, request);
        } catch (err) {
            return rejectWithValue(asError(err));
        }
    }
);

export const cancelAnnualPlanThunk = createAsyncThunk(
    'adminBilling/cancelAnnualPlan',
    async (
        { companyId, immediate }: { companyId: number, immediate?: boolean },
        { rejectWithValue },
    ) => {
        try {
            return await cancelCompanyAnnualPlan(companyId, immediate === true);
        } catch (err) {
            return rejectWithValue(asError(err));
        }
    }
);

export const getBillingEventsThunk = createAsyncThunk(
    'adminBilling/getEvents',
    async (companyId: number, { rejectWithValue }) => {
        try {
            const events = await getCompanyBillingEvents(companyId);
            return { companyId, events };
        } catch (err) {
            return rejectWithValue(asError(err));
        }
    }
);

/* Replace one row in place so a save does not blank the table it came from. */
const replaceCompany = (state: IAdminBillingSlice, company: IAdminBillingCompany) => {
    const i = state.companies.findIndex(c => c.id === company.id);
    if (i >= 0) {
        state.companies[i] = company;
    }
};

export const adminBillingSlice = createSlice({
    name: 'adminBilling',
    initialState,
    reducers: {
        clearAdminBillingError: (state) => {
            state.error = undefined;
        },
        closeBillingEvents: (state) => {
            state.events = [];
            state.eventsCompanyId = null;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(getAdminBillingThunk.pending, (state) => {
                state.status = 'loading';
            })
            .addCase(getAdminBillingThunk.fulfilled, (state, action) => {
                const payload = action.payload as IAdminBillingCompaniesResponse;
                state.status = 'idle';
                state.defaults = payload.defaults;
                state.companies = payload.companies;
            })
            .addCase(getAdminBillingThunk.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action?.payload as IErrorState;
            })
            .addCase(updateBillingDefaultsThunk.pending, (state) => {
                state.savingStatus = 'saving';
            })
            .addCase(updateBillingDefaultsThunk.fulfilled, (state, action) => {
                state.savingStatus = 'idle';
                state.defaults = action.payload as IBillingDefaults;
            })
            .addCase(updateBillingDefaultsThunk.rejected, (state, action) => {
                state.savingStatus = 'failed';
                state.error = action?.payload as IErrorState;
            })
            .addCase(updateCompanyBillingThunk.pending, (state) => {
                state.savingStatus = 'saving';
            })
            .addCase(updateCompanyBillingThunk.fulfilled, (state, action) => {
                state.savingStatus = 'idle';
                replaceCompany(state, action.payload as IAdminBillingCompany);
            })
            .addCase(updateCompanyBillingThunk.rejected, (state, action) => {
                state.savingStatus = 'failed';
                state.error = action?.payload as IErrorState;
            })
            .addCase(extendCompanyTrialThunk.pending, (state) => {
                state.savingStatus = 'saving';
            })
            .addCase(extendCompanyTrialThunk.fulfilled, (state, action) => {
                const payload = action.payload as IAdminExtendResponse;
                state.savingStatus = 'idle';
                replaceCompany(state, payload.company);
                if (state.eventsCompanyId === payload.company.id) {
                    state.events = [payload.event, ...state.events];
                }
            })
            .addCase(extendCompanyTrialThunk.rejected, (state, action) => {
                state.savingStatus = 'failed';
                state.error = action?.payload as IErrorState;
            })
            .addCase(setCompanyBillingStatusThunk.fulfilled, (state, action) => {
                state.savingStatus = 'idle';
                replaceCompany(state, action.payload as IAdminBillingCompany);
            })
            .addCase(setCompanyBillingStatusThunk.rejected, (state, action) => {
                state.savingStatus = 'failed';
                state.error = action?.payload as IErrorState;
            })
            .addCase(startCompanyTrialThunk.fulfilled, (state, action) => {
                state.savingStatus = 'idle';
                replaceCompany(state, (action.payload as IAdminExtendResponse).company);
            })
            .addCase(startCompanyTrialThunk.rejected, (state, action) => {
                state.savingStatus = 'failed';
                state.error = action?.payload as IErrorState;
            })
            .addCase(setAnnualPlanThunk.pending, (state) => {
                state.savingStatus = 'saving';
            })
            .addCase(setAnnualPlanThunk.fulfilled, (state, action) => {
                state.savingStatus = 'idle';
                replaceCompany(state, action.payload as IAdminBillingCompany);
            })
            .addCase(setAnnualPlanThunk.rejected, (state, action) => {
                state.savingStatus = 'failed';
                state.error = action?.payload as IErrorState;
            })
            .addCase(cancelAnnualPlanThunk.pending, (state) => {
                state.savingStatus = 'saving';
            })
            .addCase(cancelAnnualPlanThunk.fulfilled, (state, action) => {
                state.savingStatus = 'idle';
                replaceCompany(state, action.payload as IAdminBillingCompany);
            })
            .addCase(cancelAnnualPlanThunk.rejected, (state, action) => {
                state.savingStatus = 'failed';
                state.error = action?.payload as IErrorState;
            })
            .addCase(getBillingEventsThunk.fulfilled, (state, action) => {
                const payload = action.payload as { companyId: number, events: IBillingEvent[] };
                state.eventsCompanyId = payload.companyId;
                state.events = payload.events;
            })
            .addCase(getBillingEventsThunk.rejected, (state, action) => {
                state.error = action?.payload as IErrorState;
            });
    },
});

export const { clearAdminBillingError, closeBillingEvents } = adminBillingSlice.actions;

export const selectAdminBillingState = (state: RootState) => state.adminBilling;

export default adminBillingSlice.reducer;
